import { motion } from 'framer-motion';
import {
  Zap,
  Brain,
  Clock,
  Shield,
  BarChart3,
  Globe,
  Puzzle,
  Sparkles,
  MessageSquare,
  RefreshCcw,
  Lock,
  Layers,
} from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const features = [
  {
    icon: Brain,
    title: 'Advanced AI Models',
    description:
      'Powered by state-of-the-art language models that understand context, nuance, and your business needs.',
    color: 'purple',
  },
  {
    icon: Clock,
    title: '24/7 Availability',
    description:
      'Your AI employees never sleep. They work around the clock, across time zones, without breaks.',
    color: 'cyan',
  },
  {
    icon: Zap,
    title: 'Instant Responses',
    description:
      'Get answers and completed tasks in seconds, not hours. Speed up your entire workflow.',
    color: 'yellow',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description:
      'Bank-level encryption, SOC 2 compliance, and strict data privacy protocols protect your information.',
    color: 'green',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Insights',
    description:
      'Track performance, measure ROI, and optimize your AI team with detailed analytics dashboards.',
    color: 'blue',
  },
  {
    icon: Globe,
    title: 'Multi-language Support',
    description:
      'Communicate with customers worldwide in 50+ languages with natural, fluent translations.',
    color: 'pink',
  },
  {
    icon: Puzzle,
    title: 'Easy Integrations',
    description:
      'Connect with your favorite tools — Slack, Zapier, HubSpot, Salesforce, and 100+ more.',
    color: 'orange',
  },
  {
    icon: Sparkles,
    title: 'Custom Training',
    description:
      'Train your AI employees on your brand voice, products, and processes for personalized responses.',
    color: 'violet',
  },
  {
    icon: MessageSquare,
    title: 'Natural Conversations',
    description:
      'AI that sounds human. Your customers won\'t know the difference between AI and human support.',
    color: 'teal',
  },
  {
    icon: RefreshCcw,
    title: 'Continuous Learning',
    description:
      'Your AI employees get smarter over time, learning from every interaction to improve performance.',
    color: 'rose',
  },
  {
    icon: Lock,
    title: 'Access Controls',
    description:
      'Fine-grained permissions let you control exactly what each team member can access and modify.',
    color: 'slate',
  },
  {
    icon: Layers,
    title: 'Scalable Infrastructure',
    description:
      'Handle 10 requests or 10 million. Our infrastructure scales automatically with your needs.',
    color: 'emerald',
  },
];

const colorClasses: Record<string, { bg: string; text: string; textLight: string; border: string; borderLight: string }> = {
  purple: { bg: 'bg-purple-500/10', text: 'text-purple-400', textLight: 'text-purple-600', border: 'border-purple-500/20', borderLight: 'border-purple-200' },
  cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', textLight: 'text-cyan-600', border: 'border-cyan-500/20', borderLight: 'border-cyan-200' },
  yellow: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', textLight: 'text-yellow-600', border: 'border-yellow-500/20', borderLight: 'border-yellow-200' },
  green: { bg: 'bg-green-500/10', text: 'text-green-400', textLight: 'text-green-600', border: 'border-green-500/20', borderLight: 'border-green-200' },
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', textLight: 'text-blue-600', border: 'border-blue-500/20', borderLight: 'border-blue-200' },
  pink: { bg: 'bg-pink-500/10', text: 'text-pink-400', textLight: 'text-pink-600', border: 'border-pink-500/20', borderLight: 'border-pink-200' },
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-400', textLight: 'text-orange-600', border: 'border-orange-500/20', borderLight: 'border-orange-200' },
  violet: { bg: 'bg-violet-500/10', text: 'text-violet-400', textLight: 'text-violet-600', border: 'border-violet-500/20', borderLight: 'border-violet-200' },
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-400', textLight: 'text-teal-600', border: 'border-teal-500/20', borderLight: 'border-teal-200' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-400', textLight: 'text-rose-600', border: 'border-rose-500/20', borderLight: 'border-rose-200' },
  slate: { bg: 'bg-slate-500/10', text: 'text-slate-400', textLight: 'text-slate-600', border: 'border-slate-500/20', borderLight: 'border-slate-200' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', textLight: 'text-emerald-600', border: 'border-emerald-500/20', borderLight: 'border-emerald-200' },
};

export default function Features() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title="Features"
      subtitle="Everything you need to automate your business with AI employees."
    >
      {/* Features Grid */}
      <section className="mb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const colors = colorClasses[feature.color];
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-6 rounded-2xl border transition-all group ${
                  isDark
                    ? `bg-dark-800/50 ${colors.border} hover:border-opacity-50`
                    : `bg-white ${colors.borderLight} shadow-sm hover:shadow-md`
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <feature.icon className={`w-6 h-6 ${isDark ? colors.text : colors.textLight}`} />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{feature.title}</h3>
                <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Comparison Section */}
      <section className={`mb-16 p-6 rounded-2xl border ${
        isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
      }`}>
        <h2 className={`text-2xl font-bold mb-8 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
          SweetPilot vs Traditional Hiring
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                <th className={`text-left py-4 px-4 font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Aspect</th>
                <th className={`text-center py-4 px-4 font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>SweetPilot AI</th>
                <th className={`text-center py-4 px-4 font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  Traditional Hire
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-slate-100'}`}>
              {[
                ['Setup Time', 'Minutes', 'Weeks to months'],
                ['Availability', '24/7/365', 'Business hours'],
                ['Monthly Cost', 'From $29', '$3,000-$8,000+'],
                ['Scaling', 'Instant', 'Hire more staff'],
                ['Training', 'Pre-trained', 'Weeks of onboarding'],
                ['Consistency', '100% consistent', 'Variable'],
              ].map(([aspect, ai, traditional]) => (
                <tr key={aspect} className={isDark ? 'hover:bg-dark-800/30' : 'hover:bg-slate-50'}>
                  <td className={`py-4 px-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{aspect}</td>
                  <td className={`py-4 px-4 text-center ${isDark ? 'text-green-400' : 'text-green-600'}`}>{ai}</td>
                  <td className={`py-4 px-4 text-center ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>{traditional}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Ready to Transform Your Business?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Start with our free plan and experience the power of AI employees.
        </p>
        <a
          href="/pricing"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Get Started
        </a>
      </section>
    </StaticPageLayout>
  );
}
